// ShiftRosterUI.jsx
import React, { useState, useEffect } from 'react';
import { Box, Grid, Button, Typography, TextField, MenuItem, Paper, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import ShiftReport from './ShiftReport';
export default function ShiftRosterUI() {
  const [departments, setDepartments] = useState([]);
  const [deptId, setDeptId] = useState('');
  const [startDate, setStartDate] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [rows, setRows] = useState([]);
  const [shifts, setShifts] = useState([]);

  useEffect(() => {
    // Mock lookups
    setDepartments([
      { id: 1, name: 'Production' },
      { id: 2, name: 'Maintenance' },
      { id: 3, name: 'Quality Control' },
      { id: 4, name: 'Packing' },
    ]);
    setShifts([
      { id: 1, code: 'A', name: 'Morning (6 AM - 2 PM)' },
      { id: 2, code: 'B', name: 'Afternoon (2 PM - 10 PM)' },
      { id: 3, code: 'C', name: 'Night (10 PM - 6 AM)' },
      { id: 4, code: 'G', name: 'General (9 AM - 6 PM)' },
    ]);
  }, []);

  // 30 sample employees distributed across 4 departments
  const sampleEmployees = Array.from({ length: 30 }, (_, i) => ({
    id: i + 1,
    name: `Employee ${i + 1}`,
    deptId: (i % 4) + 1,
    email: `employee${i + 1}@example.com`,
  }));

  function generateRoster() {
    if (!deptId) return alert('Please select department');

    const deptEmployees = sampleEmployees.filter(e => e.deptId === Number(deptId));
    const daysInRange = dayjs(endDate).diff(dayjs(startDate), 'day') + 1;

    let newRows = [];
    const shiftCycle = [1, 2, 3]; // A -> B -> C rotation

    deptEmployees.forEach((emp, empIndex) => {
      for (let d = 0; d < daysInRange; d++) {
        const date = dayjs(startDate).add(d, 'day').format('YYYY-MM-DD');
        const shiftId = shiftCycle[(empIndex + d) % shiftCycle.length];
        const isWeekoff = (d + empIndex) % 7 === 6; // simple weekly off pattern
        newRows.push({
          id: `${emp.id}-${date}`,
          employeeId: emp.id,
          employee: emp.name,
          date,
          shiftId,
          shiftName: shifts.find(s => s.id === shiftId)?.name || '',
          isWeekoff,
        });
      }
    });

    setRows(newRows);
  }

  const columns = [
    { field: 'date', headerName: 'Date', width: 120 },
    { field: 'employee', headerName: 'Employee', width: 220 },
    {
      field: 'shiftName',
      headerName: 'Shift',
      width: 260,
      renderCell: (params) => (
        <TextField
          select
          size="small"
          value={params.row.shiftId}
          onChange={(e) => {
            const newShiftId = Number(e.target.value);
            setRows(prev => prev.map(r =>
              r.id === params.row.id ? {
                ...r,
                shiftId: newShiftId,
                shiftName: shifts.find(s => s.id === newShiftId)?.name || '',
              } : r
            ));
          }}
          sx={{ minWidth: 220 }}
        >
          {shifts.map(s => (
            <MenuItem key={s.id} value={s.id}>{s.code} - {s.name}</MenuItem>
          ))}
        </TextField>
      ),
    },
    {
      field: 'isWeekoff',
      headerName: 'Week Off',
      width: 120,
      renderCell: (params) => (
        params.value ? <Chip label="Off" color="success" size="small" /> : <Chip label="Work" size="small" />
      ),
    },
  ];

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight="bold">Shift Roster — Paper Mill (Demo)</Typography>
      <Paper sx={{ p: 2, mt: 2 }}>
        <Grid container spacing={2}>
          <Grid item>
            <TextField
              select
              label="Department"
              value={deptId}
              onChange={(e) => setDeptId(e.target.value)}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value=''>-- Select Department --</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item>
            <Button variant="contained" onClick={generateRoster}>Generate Roster</Button>
          </Grid>
          <Grid item>
            <Button color="success" variant="outlined" onClick={() => alert('Roster Published (Demo)')}>Publish</Button>
          </Grid>
        </Grid>
      </Paper>

      <Box mt={3} height={520}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(r) => r.id}
          pageSize={25}
          rowsPerPageOptions={[25, 50]}
        />
      </Box>
    </Box>
  );
}
